/**
 * Configuración de API para LR Marketing Portfolio
 * Conexión con MySQL a través de PHP
 */

// Configuración de la API
const API_CONFIG = {
    // URL base de la API (cambiar según tu dominio)
    BASE_URL: 'https://lrmarketing.pro/api', // Cambiar por tu dominio real
    
    // Endpoints
    ENDPOINTS: {
        // Autenticación
        LOGIN: '/auth.php',
        USER_INFO: '/auth.php',
        CHANGE_PASSWORD: '/auth.php',
        
        // Proyectos
        PROJECTS: '/projects.php',
        PROJECT_BY_ID: '/projects.php',
        PROJECT_STATS: '/projects.php?stats=1',
        
        // Categorías
        CATEGORIES: '/categories.php',
        
        // Configuraciones
        SETTINGS: '/settings.php',
        
        // Contactos y suscripciones
        CONTACTS: '/contacts.php',
        SUBSCRIPTIONS: '/contacts.php'
    },
    
    // Headers por defecto
    DEFAULT_HEADERS: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
};

// Clase para manejar las peticiones HTTP
class ApiService {
    constructor() {
        this.baseURL = API_CONFIG.BASE_URL;
        this.token = localStorage.getItem('authToken');
    }

    // Método para hacer peticiones HTTP
    async request(endpoint, options = {}) {
        const url = `${this.baseURL}${endpoint}`;
        
        // Configurar headers
        const headers = {
            ...API_CONFIG.DEFAULT_HEADERS,
            ...options.headers
        };

        // Agregar token de autorización si existe
        if (this.token) {
            headers['Authorization'] = `Bearer ${this.token}`;
        }

        const config = {
            method: options.method || 'GET',
            headers,
            ...options
        };

        // Agregar body para POST, PUT, PATCH
        if (options.body && ['POST', 'PUT', 'PATCH'].includes(config.method)) {
            config.body = JSON.stringify(options.body);
        }

        try {
            const response = await fetch(url, config);
            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Error en la petición');
            }

            return data;
        } catch (error) {
            console.error('Error en API:', error);
            throw error;
        }
    }

    // Autenticación
    async login(username, password) {
        const response = await this.request(API_CONFIG.ENDPOINTS.LOGIN, {
            method: 'POST',
            body: {
                action: 'login',
                username,
                password
            }
        });

        if (response.success) {
            this.token = response.token;
            localStorage.setItem('authToken', response.token);
            localStorage.setItem('user', JSON.stringify(response.user));
        }

        return response;
    }

    async getUserInfo() {
        return await this.request(API_CONFIG.ENDPOINTS.USER_INFO);
    }

    async changePassword(currentPassword, newPassword) {
        return await this.request(API_CONFIG.ENDPOINTS.CHANGE_PASSWORD, {
            method: 'POST',
            body: {
                action: 'change_password',
                current_password: currentPassword,
                new_password: newPassword
            }
        });
    }

    logout() {
        this.token = null;
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        localStorage.removeItem('rememberMe');
    }

    // Proyectos
    async getProjects(categoryId = null, limit = null) {
        let endpoint = API_CONFIG.ENDPOINTS.PROJECTS;
        const params = new URLSearchParams();
        
        if (categoryId) params.append('category_id', categoryId);
        if (limit) params.append('limit', limit);
        
        if (params.toString()) {
            endpoint += `?${params.toString()}`;
        }

        return await this.request(endpoint);
    }

    async getProjectById(id) {
        return await this.request(`${API_CONFIG.ENDPOINTS.PROJECT_BY_ID}?id=${id}`);
    }

    async createProject(projectData) {
        return await this.request(API_CONFIG.ENDPOINTS.PROJECTS, {
            method: 'POST',
            body: projectData
        });
    }

    async updateProject(id, projectData) {
        return await this.request(`${API_CONFIG.ENDPOINTS.PROJECTS}?id=${id}`, {
            method: 'PUT',
            body: { ...projectData, id }
        });
    }

    async deleteProject(id) {
        return await this.request(`${API_CONFIG.ENDPOINTS.PROJECTS}?id=${id}`, {
            method: 'DELETE'
        });
    }

    async getProjectStats() {
        return await this.request(API_CONFIG.ENDPOINTS.PROJECT_STATS);
    }

    // Categorías
    async getCategories() {
        return await this.request(API_CONFIG.ENDPOINTS.CATEGORIES);
    }

    // Configuraciones
    async getSettings() {
        return await this.request(API_CONFIG.ENDPOINTS.SETTINGS);
    }

    async updateSettings(settings) {
        return await this.request(API_CONFIG.ENDPOINTS.SETTINGS, {
            method: 'PUT',
            body: settings
        });
    }
    
    // Contactos
    async submitContact(contactData) {
        return await this.request(`${API_CONFIG.ENDPOINTS.CONTACTS}?action=contact`, {
            method: 'POST',
            body: contactData
        });
    }
    
    async getContacts(limit = null, offset = 0) {
        return await this.request(`${API_CONFIG.ENDPOINTS.CONTACTS}?action=contacts&limit=${limit}&offset=${offset}`, {
            method: 'GET'
        });
    }
    
    async updateContactStatus(id, status) {
        return await this.request(`${API_CONFIG.ENDPOINTS.CONTACTS}?action=update-status`, {
            method: 'PUT',
            body: { id, status }
        });
    }
    
    // Suscripciones
    async submitSubscription(subscriptionData) {
        return await this.request(`${API_CONFIG.ENDPOINTS.SUBSCRIPTIONS}?action=subscription`, {
            method: 'POST',
            body: subscriptionData
        });
    }
    
    // Estadísticas de contactos y suscripciones
    async getContactStats() {
        return await this.request(`${API_CONFIG.ENDPOINTS.CONTACTS}?action=stats`, {
            method: 'GET'
        });
    }

    // Verificar si el usuario está autenticado
    isAuthenticated() {
        return !!this.token;
    }

    // Obtener token actual
    getToken() {
        return this.token;
    }

    // Actualizar token (para cuando se renueva)
    updateToken(newToken) {
        this.token = newToken;
        localStorage.setItem('authToken', newToken);
    }
}

// Instancia global del servicio API
const apiService = new ApiService();

// Exportar para uso en componentes Vue
export default apiService;

// También exportar la configuración por si se necesita
export { API_CONFIG }; 